import ReactGoogleAutocomplete from "react-google-autocomplete";
import { useState, useEffect } from "react";
import "./styles.scss";
import OliverText, { TextType } from "../OliverText";

// Helper function to extract address component by type (returns long_name)
export const getAddressComponent = (place: Place, types: string[]) => {
    if (!place.address_components || !Array.isArray(place.address_components)) {
        return "";
    }
    const component = place.address_components.find(comp => types.some(type => comp.types.includes(type)));
    return component?.long_name || "";
};

// Helper function to extract address component short_name (useful for country codes)
export const getAddressComponentShort = (place: Place, types: string[]) => {
    if (!place.address_components || !Array.isArray(place.address_components)) {
        return "";
    }
    const component = place.address_components.find(comp => types.some(type => comp.types.includes(type)));
    return component?.short_name || "";
};

export type Place = {
    formatted_address: string;
    address_components?: Array<{
        types: string[];
        long_name: string;
        short_name: string;
    }>;
    geometry?: {
        location: {
            lat: () => number;
            lng: () => number;
        };
    };
};

type Props = {
    onChange: (place: Place) => void;
    placeholder?: string;
    geoType?: string;
    value?: string;
    country?: string;
    label?: string;
};

function AutocompleteInput({ onChange, placeholder = "", geoType = "geocode", value = "", country = "", label = "" }: Props) {
    const [inputValue, setInputValue] = useState(value || "");

    const options: any = { types: [geoType] };

    if (country) {
        options.componentRestrictions = { country };
    }

    // Update internal state when external value changes
    useEffect(() => {
        setInputValue(value || "");
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        // Update parent state with manually typed address even if autocomplete doesn't work
        // This allows manual entry to work when autocomplete fails
        onChange({ 
            formatted_address: newValue,
            // No address_components when manually typing - parent should handle this
        });
    };

    const handlePlaceSelected = (place: any) => {
        if (!place) {
            return;
        }

        // Convert Google Places API result to our Place type
        const convertedPlace: Place = {
            formatted_address: place.formatted_address || "",
            address_components: place.address_components?.map((comp: any) => ({
                types: comp.types || [],
                long_name: comp.long_name || "",
                short_name: comp.short_name || ""
            })),
            geometry: place.geometry
                ? {
                      location: {
                          lat: () => place.geometry?.location?.lat() || 0,
                          lng: () => place.geometry?.location?.lng() || 0
                      }
                  }
                : undefined
        };

        setInputValue(convertedPlace.formatted_address);
        onChange(convertedPlace);
    };

    return (
        <div className="autocomplete-input-container">
            {label && (
                <OliverText type={TextType.BODY_LIGHT_XS} color="base-grey">
                    {label}
                </OliverText>
            )}
            <ReactGoogleAutocomplete
                apiKey={import.meta.env.VITE_GOOGLE_API_KEY}
                onPlaceSelected={handlePlaceSelected}
                onChange={handleInputChange}
                className="autocompleteInput"
                options={options}
                placeholder={placeholder}
                value={inputValue} // Controlled input
            />
        </div>
    );
}

export default AutocompleteInput;
